/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { debounce } from 'lodash';
import type { Feature, MapBrowserEvent } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { Vector } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import type { StyleLike } from 'ol/style/Style';
import { useContext, useEffect, useRef, useState } from 'react';
import MapContext from '../context/MapContext';
import type { ClickOnFeature, HoverSelect } from '../utils/types';

export interface FeatureLayerProps {
  features: Feature[];
  style: StyleLike;
  zIndex?: number;
  hoverSelect?: HoverSelect;
  clickOnFeature?: ClickOnFeature;
  testId?: string;
  opacity?: number;
  declutter?: boolean;
  visible?: boolean;
}

export const FeatureLayer: React.FC<FeatureLayerProps> = ({
  features,
  style,
  zIndex,
  hoverSelect,
  clickOnFeature,
  testId,
  opacity,
  declutter = false,
  visible = true,
}: FeatureLayerProps) => {
  const { map } = useContext(MapContext);
  const [source, setSource] = useState<VectorSource>();

  const layerRef =
    useRef<Vector<VectorSource<Feature<Geometry>>, Feature<Geometry>>>(
      undefined,
    );

  useEffect(() => {
    if (!map || !visible) {
      return (): void => {};
    }
    const source = new VectorSource<Feature>({
      features: [],
    });

    const layer = new Vector({
      source,
      zIndex,
      style,
      properties: {
        testId,
      },
      opacity,
      declutter,
      visible,
    });
    layerRef.current = layer;

    layer.set('layerType', 'FeatureLayer');

    setSource(source);
    map.addLayer(layer);

    return (): void => {
      setSource(undefined);
      source.clear();
      map.removeLayer(layer);
    };
    // NOTE: style should not be part of this effect, as it changes always, causing the layer to be add/removed continuousely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, zIndex, testId, visible]);

  useEffect(() => {
    // Handle style function:
    if (layerRef.current && style) {
      layerRef.current?.setStyle(style);
    }
  }, [style]);

  useEffect(() => {
    // Handle opacity:
    if (layerRef.current && opacity !== undefined) {
      layerRef.current?.setOpacity(opacity);
    }
  }, [opacity]);

  // Layer visible useEffect
  useEffect(() => {
    layerRef.current?.setVisible(visible);
  }, [visible]);

  useEffect(() => {
    if (!source) {
      return;
    }
    source.clear();
    if (features && features.length > 0) {
      source.addFeatures(features);
    }
  }, [source, features]);

  useEffect((): (() => void) => {
    if (!map || !source || !hoverSelect || !hoverSelect.active || !visible) {
      return (): void => {};
    }

    const previousHover: FeatureLike[] = [];

    const pointerMoveFunction = debounce(
      (evt: MapBrowserEvent<KeyboardEvent | WheelEvent | PointerEvent>) => {
        const hoveredFeature = map.forEachFeatureAtPixel(
          evt.pixel,
          (feature: FeatureLike, layer): FeatureLike | false => {
            if (!layer || layer?.getSource() !== source) {
              return false;
            }
            return feature;
          },
        );

        if (hoveredFeature && previousHover.includes(hoveredFeature)) {
          return;
        }

        const unhovered = previousHover.length > 0;

        previousHover.forEach((feature) => {
          (feature as Feature).set('hover', false);
        });
        previousHover.splice(0);

        if (hoveredFeature) {
          previousHover.push(hoveredFeature);
          (hoveredFeature as Feature).set('hover', true);
          hoverSelect.onHover?.(hoveredFeature as Feature);
        } else if (unhovered) {
          hoverSelect.onHover?.(undefined);
        }
      },
      5,
      {},
    );

    const eventKey = map.on('pointermove', pointerMoveFunction);

    return () => {
      pointerMoveFunction.cancel();
      source.getFeatures().forEach((feature) => {
        (feature as Feature).set('hover', false);
      });
      unByKey(eventKey);
      hoverSelect.onHover?.(undefined);
    };
  }, [map, source, hoverSelect, visible]);

  useEffect((): (() => void) => {
    if (!map || !source || !clickOnFeature || !visible) {
      return (): void => {};
    }

    const clickFunction = (
      evt: MapBrowserEvent<KeyboardEvent | WheelEvent | PointerEvent>,
    ): void => {
      const features: Feature[] = [];
      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature) => {
          features.push(feature as Feature);
        },
        { hitTolerance: clickOnFeature.hitTolerance },
      );

      const isShiftClick =
        (evt.originalEvent as PointerEvent).shiftKey ?? false;
      clickOnFeature.onClick(features, isShiftClick);
    };

    const eventKey = map.on('click', clickFunction);

    return (): void => {
      unByKey(eventKey);
    };
  }, [map, source, clickOnFeature, visible]);

  return null;
};
